import api from './api';

export interface ResourceType {
  id: string;
  name: string;
  description?: string;
  icon: string;
  color: string;
  resourceCount: number;
}

export interface Resource {
  id: string;
  name: string;
  description?: string;
  resourceTypeId: string;
  resourceTypeName: string;
  resourceTypeIcon: string;
  capacity: number;
  capacityUnit: string;
  location?: string;
  isAvailable: boolean;
  color?: string;
  activeBookingsCount: number;
  createdAt: string;
}

export interface ResourceBooking {
  id: string;
  resourceId: string;
  resourceName: string;
  taskId?: string;
  taskTitle?: string;
  bookedByUserId: string;
  bookedByUserName: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  quantity?: number;
  status: string;
  isRecurring: boolean;
  createdAt: string;
}

export interface ResourceTimeline {
  resourceId: string;
  resourceName: string;
  resourceTypeName: string;
  color?: string;
  bookings: ResourceBooking[];
}

const resourceService = {
  // Resource Types
  getResourceTypes: async (): Promise<ResourceType[]> => {
    const response = await api.get<ResourceType[]>('/resourcetypes');
    return response.data;
  },

  createResourceType: async (data: { name: string; description?: string; icon?: string; color?: string }): Promise<ResourceType> => {
    const response = await api.post<ResourceType>('/resourcetypes', data);
    return response.data;
  },

  deleteResourceType: async (id: string): Promise<void> => {
    await api.delete(`/resourcetypes/${id}`);
  },

  // Resources
  getResources: async (typeId?: string, available?: boolean): Promise<Resource[]> => {
    const params = new URLSearchParams();
    if (typeId) params.append('typeId', typeId);
    if (available !== undefined) params.append('available', String(available));
    const response = await api.get<Resource[]>(`/resources?${params}`);
    return response.data;
  },

  getResource: async (id: string): Promise<Resource> => {
    const response = await api.get<Resource>(`/resources/${id}`);
    return response.data;
  },

  createResource: async (data: {
    name: string; description?: string; resourceTypeId: string;
    capacity?: number; capacityUnit?: string; location?: string; color?: string;
  }): Promise<Resource> => {
    const response = await api.post<Resource>('/resources', data);
    return response.data;
  },

  updateResource: async (id: string, data: any): Promise<Resource> => {
    const response = await api.put<Resource>(`/resources/${id}`, data);
    return response.data;
  },

  deleteResource: async (id: string): Promise<void> => {
    await api.delete(`/resources/${id}`);
  },

  // Bookings
  getBookings: async (resourceId: string, start?: string, end?: string): Promise<ResourceBooking[]> => {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    const response = await api.get<ResourceBooking[]>(`/resources/${resourceId}/bookings?${params}`);
    return response.data;
  },

  createBooking: async (resourceId: string, data: {
    title: string; description?: string; taskId?: string;
    startTime: string; endTime: string; quantity?: number; status?: string;
  }): Promise<ResourceBooking> => {
    const response = await api.post<ResourceBooking>(`/resources/${resourceId}/bookings`, data);
    return response.data;
  },

  updateBooking: async (bookingId: string, data: any): Promise<ResourceBooking> => {
    const response = await api.put<ResourceBooking>(`/resources/bookings/${bookingId}`, data);
    return response.data;
  },

  deleteBooking: async (bookingId: string): Promise<void> => {
    await api.delete(`/resources/bookings/${bookingId}`);
  },

  // Timeline
  getTimeline: async (start?: string, end?: string): Promise<ResourceTimeline[]> => {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    const response = await api.get<ResourceTimeline[]>(`/resources/timeline?${params}`);
    return response.data;
  },

  // Availability
  checkAvailability: async (resourceId: string, start: string, end: string): Promise<{
    resourceId: string; resourceName: string; isAvailable: boolean;
    conflictingBookings: { id: string; title: string; startTime: string; endTime: string }[];
  }> => {
    const response = await api.get(`/resources/${resourceId}/availability?start=${start}&end=${end}`);
    return response.data;
  },
};

export default resourceService;
